import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  CheckCircle2,
  CircleHelp,
  Cloud,
  ExternalLink,
  KeyRound,
  LayoutDashboard,
  Loader2,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Users,
  XCircle,
} from 'lucide-react';

import DirectoryIdentityHealth from '../components/DirectoryIdentityHealth';
import DirectorySyncOperations from '../components/DirectorySyncOperations';
import {
  EmptyState,
  InlineAlert,
  MetricCard,
  PageHeader,
  SearchInput,
  SegmentedTabs,
  StatusPill,
  cx,
  formatDateTime,
} from '../components/EnterpriseAdminUI';
import { identityAPI } from '../services/api';

const PAGE_TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'setup', label: 'Microsoft setup', icon: ShieldCheck },
  { id: 'sync', label: 'Sync & health', icon: Activity },
  { id: 'directory', label: 'Directory & RBAC', icon: Users },
];

const DIRECTORY_TABS = [
  { id: 'users', label: 'Users' },
  { id: 'groups', label: 'Groups' },
  { id: 'mappings', label: 'Mappings' },
  { id: 'advanced', label: 'Advanced import' },
];

function normaliseDirectory(payload = {}) {
  return {
    users: Array.isArray(payload.users) ? payload.users : [],
    groups: Array.isArray(payload.groups) ? payload.groups : [],
    mappings: Array.isArray(payload.mappings) ? payload.mappings : [],
    pii_groups: Array.isArray(payload.pii_groups) ? payload.pii_groups : [],
    sync_runs: Array.isArray(payload.sync_runs) ? payload.sync_runs : [],
    setup: payload.setup || {},
    health: payload.health || null,
  };
}

function parseLines(value) {
  return String(value || '')
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function MicrosoftConnectionSummary({ microsoft, setup }) {
  const connected = Boolean(microsoft?.connected);
  const tenant = microsoft?.tenant_domain || microsoft?.tenant_id || setup?.configuration?.tenant_id;

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <div
            className={cx(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ring-1',
              connected
                ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                : 'bg-amber-50 text-amber-700 ring-amber-100',
            )}
          >
            <Cloud className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-black text-slate-950">Microsoft Entra ID</h2>
              <StatusPill
                label={connected ? 'Connected' : 'Connection pending'}
                tone={connected ? 'success' : 'warning'}
              />
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Microsoft authenticates users. PII Platform remains the source of truth for local groups,
              permissions, and authorization decisions.
            </p>
            {tenant && <p className="mt-2 break-all font-mono text-xs text-slate-500">{tenant}</p>}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <StatusPill
            label={setup?.all_confirmed ? 'Setup confirmed' : 'Setup requires review'}
            tone={setup?.all_confirmed ? 'success' : 'warning'}
          />
          <StatusPill
            label={setup?.service_principal?.resolved ? 'Enterprise app resolved' : 'Enterprise app unresolved'}
            tone={setup?.service_principal?.resolved ? 'success' : 'neutral'}
          />
        </div>
      </div>
    </div>
  );
}

function SetupConfirmationCard({ item, note, onNoteChange, onUpdate, busy }) {
  const confirmed = Boolean(item.confirmed ?? item.done);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <div
          className={cx(
            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            confirmed ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400',
          )}
        >
          {confirmed ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-black text-slate-950">{item.label}</p>
              {item.description && (
                <p className="mt-1 text-xs leading-5 text-slate-500">{item.description}</p>
              )}
              {item.confirmed_at && (
                <p className="mt-2 text-xs text-slate-400">
                  Confirmed {formatDateTime(item.confirmed_at)}
                </p>
              )}
            </div>

            <button
              type="button"
              disabled={busy}
              aria-label={`${confirmed ? 'Revoke confirmation' : 'Confirm completed'} for ${item.label}`}
              onClick={() => onUpdate(item.key, !confirmed, note)}
              className={cx(
                'shrink-0 rounded-xl px-3 py-2 text-xs font-bold disabled:opacity-60',
                confirmed
                  ? 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  : 'bg-orange-600 text-white hover:bg-orange-700',
              )}
            >
              {busy ? 'Saving…' : confirmed ? 'Revoke confirmation' : 'Confirm completed'}
            </button>
          </div>

          <label className="mt-3 block text-xs font-bold text-slate-600">
            Administrator note
            <textarea
              value={note}
              aria-label={`Note for ${item.label}`}
              onChange={(event) => onNoteChange(item.key, event.target.value)}
              rows={2}
              placeholder="Optional implementation note or ticket reference"
              className="mt-1 w-full resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
            />
          </label>
        </div>
      </div>
    </div>
  );
}

function ConfigurationValue({ label, children, mono = false }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <div className={cx('mt-2 break-all text-sm text-slate-900', mono && 'font-mono text-xs')}>
        {children || '—'}
      </div>
    </div>
  );
}

export default function IdentitySetupPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [directoryTab, setDirectoryTab] = useState('users');
  const [statusData, setStatusData] = useState(null);
  const [directory, setDirectory] = useState(normaliseDirectory());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [confirmationNotes, setConfirmationNotes] = useState({});
  const [directorySearch, setDirectorySearch] = useState('');
  const [groupIdsText, setGroupIdsText] = useState('');
  const [manualUsersText, setManualUsersText] = useState('');
  const [mappingForm, setMappingForm] = useState({ directory_group: '', pii_group: '' });

  const loadAll = async ({ background = false } = {}) => {
    if (background) setRefreshing(true);
    else setLoading(true);

    setError('');

    try {
      const [statusRes, directoryRes, healthRes] = await Promise.all([
        identityAPI.getSetupStatus(),
        identityAPI.getMicrosoftDirectory(),
        identityAPI.getMicrosoftDirectoryHealth
          ? identityAPI.getMicrosoftDirectoryHealth().catch(() => null)
          : Promise.resolve(null),
      ]);

      const nextDirectory = normaliseDirectory(directoryRes.data || {});
      if (healthRes?.data) nextDirectory.health = healthRes.data;

      setStatusData(statusRes.data || {});
      setDirectory(nextDirectory);

      const notes = {};
      (nextDirectory.setup?.confirmations || []).forEach((item) => {
        notes[item.key] = item.note || '';
      });
      setConfirmationNotes(notes);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to load identity and directory configuration.',
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const organization = statusData?.organization || {};
  const microsoft = statusData?.microsoft || {};
  const sync = statusData?.sync || {};
  const rbac = statusData?.rbac_mapping || {};
  const setup = directory.setup || {};
  const confirmations = setup.confirmations || [];
  const configuration = setup.configuration || {};
  const syncSetup = setup.sync || {};

  const confirmationCompleted = confirmations.filter((item) => item.confirmed ?? item.done).length;
  const readinessPercent = confirmations.length
    ? Math.round((confirmationCompleted / confirmations.length) * 100)
    : microsoft.connected
      ? 100
      : 0;

  const filteredUsers = useMemo(() => {
    const query = directorySearch.trim().toLowerCase();
    if (!query) return directory.users;

    return directory.users.filter((user) =>
      [user.display_name, user.full_name, user.email, user.user_principal_name, user.department]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [directory.users, directorySearch]);

  const filteredGroups = useMemo(() => {
    const query = directorySearch.trim().toLowerCase();
    if (!query) return directory.groups;

    return directory.groups.filter((group) =>
      [group.display_name, group.description, group.external_id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [directory.groups, directorySearch]);

  const staleUsers = directory.users.filter((user) => user.is_stale).length;
  const staleGroups = directory.groups.filter((group) => group.is_stale).length;

  const handleConfirmation = async (key, confirmed, note) => {
    setActionLoading(`confirmation:${key}`);
    setError('');
    setSuccess('');

    try {
      await identityAPI.updateMicrosoftDirectorySetupConfirmation(key, confirmed, note || '');
      setSuccess(confirmed ? 'Setup item confirmed.' : 'Setup confirmation revoked.');
      await loadAll({ background: true });
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          'Failed to update setup confirmation.',
      );
    } finally {
      setActionLoading('');
    }
  };

  const handleSyncMode = async (mode) => {
    setActionLoading('sync-mode');
    setError('');
    setSuccess('');

    try {
      await identityAPI.updateMicrosoftDirectorySyncConfig({
        mode,
        enabled: true,
        schedule_enabled: Boolean(syncSetup.schedule_enabled),
        schedule_interval_minutes: syncSetup.schedule_interval_minutes || 360,
        selected_group_ids: syncSetup.selected_group_ids || [],
      });
      setSuccess('Directory synchronization mode updated.');
      await loadAll({ background: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to update synchronization mode.');
    } finally {
      setActionLoading('');
    }
  };

  const handleManualUserImport = async () => {
    const users = parseLines(manualUsersText);
    if (!users.length) {
      setError('Enter at least one Microsoft user object ID or UPN.');
      return;
    }

    setActionLoading('manual-users');
    setError('');
    setSuccess('');

    try {
      await identityAPI.syncMicrosoftUsers(users);
      setManualUsersText('');
      setSuccess('Manual user synchronization queued.');
      await loadAll({ background: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to queue manual user synchronization.');
    } finally {
      setActionLoading('');
    }
  };

  const handleSelectedGroupImport = async () => {
    const groupIds = parseLines(groupIdsText);
    if (!groupIds.length) {
      setError('Enter at least one Microsoft group object ID.');
      return;
    }

    setActionLoading('manual-groups');
    setError('');
    setSuccess('');

    try {
      await identityAPI.syncMicrosoftGroups(groupIds);
      setGroupIdsText('');
      setSuccess('Selected-group synchronization queued.');
      await loadAll({ background: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to queue selected-group synchronization.');
    } finally {
      setActionLoading('');
    }
  };

  const createMapping = async () => {
    if (!mappingForm.directory_group || !mappingForm.pii_group) {
      setError('Select both a Microsoft group and a PII Platform group.');
      return;
    }

    setActionLoading('create-mapping');
    setError('');
    setSuccess('');

    try {
      await identityAPI.createMicrosoftGroupMapping(
        mappingForm.directory_group,
        mappingForm.pii_group,
      );
      setMappingForm({ directory_group: '', pii_group: '' });
      setSuccess('Microsoft-to-PII group mapping created.');
      await loadAll({ background: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create group mapping.');
    } finally {
      setActionLoading('');
    }
  };

  const deleteMapping = async (mappingId) => {
    if (!window.confirm('Delete this mapping? Existing local group memberships will be retained.')) {
      return;
    }

    setActionLoading(`delete-mapping:${mappingId}`);
    setError('');
    setSuccess('');

    try {
      await identityAPI.deleteMicrosoftGroupMapping(mappingId);
      setSuccess('Mapping deleted. Existing local access was not revoked.');
      await loadAll({ background: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to delete group mapping.');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
          <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
          <span className="text-sm font-semibold text-slate-700">Loading identity workspace…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Identity governance"
        title="Identity & Directory"
        description="Configure Microsoft authentication, monitor directory synchronization, and route synchronized identities into local PII Platform RBAC without mixing setup, operations, and access assignment in one screen."
        icon={ShieldCheck}
        actions={(
          <button
            type="button"
            onClick={() => loadAll({ background: true })}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw className={cx('h-4 w-4', refreshing && 'animate-spin')} />
            Refresh workspace
          </button>
        )}
      />

      {error && (
        <InlineAlert tone="danger" title="Identity workspace action failed">
          {error}
        </InlineAlert>
      )}
      {success && (
        <InlineAlert tone="success" title="Completed">
          {success}
        </InlineAlert>
      )}

      <MicrosoftConnectionSummary microsoft={microsoft} setup={setup} />

      <SegmentedTabs
        tabs={PAGE_TABS.map((tab) => ({
          ...tab,
          badge:
            tab.id === 'directory'
              ? directory.users.length
              : tab.id === 'setup'
                ? `${confirmationCompleted}/${confirmations.length || 0}`
                : undefined,
        }))}
        activeTab={activeTab}
        onChange={setActiveTab}
        ariaLabel="Identity workspace sections"
      />

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="Setup readiness"
              value={`${readinessPercent}%`}
              hint={`${confirmationCompleted} of ${confirmations.length || 0} manual controls confirmed`}
              icon={ShieldCheck}
              tone={readinessPercent === 100 ? 'success' : 'warning'}
            />
            <MetricCard
              label="Directory users"
              value={directory.users.length || sync.directory_user_count || 0}
              hint={`${staleUsers} flagged stale for review`}
              icon={Users}
              tone={staleUsers ? 'warning' : 'brand'}
            />
            <MetricCard
              label="Directory groups"
              value={directory.groups.length || sync.directory_group_count || 0}
              hint={`${staleGroups} flagged stale for review`}
              icon={Cloud}
              tone={staleGroups ? 'warning' : 'brand'}
            />
            <MetricCard
              label="RBAC mappings"
              value={directory.mappings.length || rbac.microsoft_group_mapping_count || 0}
              hint={directory.mappings.length ? 'Automatic additive assignment enabled' : 'Individual users can be assigned locally'}
              icon={KeyRound}
              tone={directory.mappings.length ? 'success' : 'neutral'}
            />
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-black text-slate-950">Recommended administrator workflow</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Keep identity configuration and authorization assignment as separate administrative steps.
                  </p>
                </div>
                <CircleHelp className="h-5 w-5 text-slate-400" />
              </div>

              <ol className="mt-5 space-y-3">
                {[
                  ['1', 'Confirm Microsoft setup', 'Validate admin consent, Assignment required, and assigned users.'],
                  ['2', 'Run directory synchronization', 'Import users assigned to the Enterprise Application.'],
                  ['3', 'Assign local access groups', 'Use Groups & Permissions to grant the exact PII Platform role.'],
                  ['4', 'Validate Microsoft SSO', 'Login remains denied until an active local PII group exists.'],
                ].map(([step, title, description]) => (
                  <li key={step} className="flex items-start gap-3 rounded-2xl border border-slate-200 p-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-50 text-sm font-black text-orange-700">
                      {step}
                    </div>
                    <div>
                      <p className="text-sm font-black text-slate-950">{title}</p>
                      <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-black text-slate-950">Quick actions</h2>
              <p className="mt-1 text-sm text-slate-500">Jump directly to the correct administrative workspace.</p>

              <div className="mt-5 space-y-3">
                <button
                  type="button"
                  onClick={() => setActiveTab('setup')}
                  className="flex w-full items-center justify-between rounded-2xl border border-slate-200 p-4 text-left hover:bg-slate-50"
                >
                  <span>
                    <span className="block text-sm font-black text-slate-950">Review Microsoft setup</span>
                    <span className="mt-1 block text-xs text-slate-500">Deployment values and manual confirmations</span>
                  </span>
                  <Settings2 className="h-5 w-5 text-slate-400" />
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('sync')}
                  className="flex w-full items-center justify-between rounded-2xl border border-slate-200 p-4 text-left hover:bg-slate-50"
                >
                  <span>
                    <span className="block text-sm font-black text-slate-950">Open sync operations</span>
                    <span className="mt-1 block text-xs text-slate-500">Manual run, schedule, health, and history</span>
                  </span>
                  <Activity className="h-5 w-5 text-slate-400" />
                </button>

                <Link
                  to="/access-control/groups"
                  className="flex w-full items-center justify-between rounded-2xl border border-orange-200 bg-orange-50 p-4 text-left hover:bg-orange-100"
                >
                  <span>
                    <span className="block text-sm font-black text-orange-900">Assign users to local groups</span>
                    <span className="mt-1 block text-xs text-orange-800/80">Enable Microsoft SSO through local RBAC</span>
                  </span>
                  <KeyRound className="h-5 w-5 text-orange-600" />
                </Link>
              </div>
            </section>
          </div>

          <InlineAlert tone="warning" title="Automatic deprovisioning remains disabled">
            Missing Microsoft objects are marked stale for administrator review. The platform does not automatically
            remove local groups, disable accounts, or revoke sessions in this release.
          </InlineAlert>
        </div>
      )}

      {activeTab === 'setup' && (
        <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
          <section className="space-y-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-black text-slate-950">Deployment configuration</h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Values shown here come from the active platform deployment and connected customer tenant.
                </p>
              </div>
              {configuration.admin_center_url && (
                <a
                  href={configuration.admin_center_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                >
                  Entra admin centre
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <ConfigurationValue label="Application / client ID" mono>
                {configuration.client_id}
              </ConfigurationValue>
              <ConfigurationValue label="Connected tenant ID" mono>
                {configuration.tenant_id || microsoft.tenant_id}
              </ConfigurationValue>
              <ConfigurationValue label="Enterprise Application">
                {configuration.enterprise_application_name}
              </ConfigurationValue>
              <ConfigurationValue label="Sync mode">
                {syncSetup.mode || sync.mode || 'assigned_enterprise_app'}
              </ConfigurationValue>
              <div className="sm:col-span-2">
                <ConfigurationValue label="Redirect / application URL" mono>
                  {configuration.redirect_uri}
                </ConfigurationValue>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-black text-slate-950">Required Microsoft Graph permissions</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {(configuration.required_graph_permissions || []).map((permission) => (
                  <StatusPill key={permission} label={permission} tone="info" />
                ))}
                {!(configuration.required_graph_permissions || []).length && (
                  <p className="text-sm text-slate-500">No permission list was returned by the deployment.</p>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-black text-slate-950">Administrator steps</h3>
              <ol className="mt-3 space-y-2">
                {(setup.instructions || []).map((instruction, index) => (
                  <li key={`${instruction}-${index}`} className="flex items-start gap-3 rounded-xl bg-slate-50 p-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-xs font-black text-slate-700 ring-1 ring-slate-200">
                      {index + 1}
                    </span>
                    <span className="text-sm leading-6 text-slate-700">{instruction}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-black text-slate-950">Manual setup confirmations</h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Confirm only after the corresponding Entra configuration has been completed.
                </p>
              </div>
              <StatusPill
                label={`${confirmationCompleted}/${confirmations.length || 0} complete`}
                tone={setup.all_confirmed ? 'success' : 'warning'}
              />
            </div>

            <div className="mt-5 space-y-3">
              {confirmations.length ? (
                confirmations.map((item) => (
                  <SetupConfirmationCard
                    key={item.key}
                    item={item}
                    note={confirmationNotes[item.key] || ''}
                    onNoteChange={(key, value) =>
                      setConfirmationNotes((previous) => ({ ...previous, [key]: value }))
                    }
                    onUpdate={handleConfirmation}
                    busy={actionLoading === `confirmation:${item.key}`}
                  />
                ))
              ) : (
                <EmptyState
                  icon={CircleHelp}
                  title="No setup checklist returned"
                  description="The connected provider did not return manual setup controls. Refresh after connecting Microsoft Entra ID."
                />
              )}
            </div>
          </section>
        </div>
      )}

      {activeTab === 'sync' && (
        <div className="space-y-6">
          <DirectoryIdentityHealth health={directory.health} />
          <DirectorySyncOperations
            setup={setup}
            initialRuns={directory.sync_runs || []}
            onRefresh={() => loadAll({ background: true })}
          />
        </div>
      )}

      {activeTab === 'directory' && (
        <div className="space-y-5">
          <InlineAlert tone="info" title="Use local groups for authorization">
            Microsoft-synced users do not automatically become administrators. Assign an imported user to a local PII
            group from <Link className="font-bold underline" to="/access-control/groups">Groups & Permissions</Link> to
            enable Microsoft SSO and grant only the required local permissions.
          </InlineAlert>

          <InlineAlert tone="warning" title="Automatic deprovisioning is not enabled">
            Stale Microsoft users, groups, and memberships remain visible for review. Synchronization does not remove
            existing local PII groups, disable local accounts, or revoke sessions in this release.
          </InlineAlert>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <SegmentedTabs
                tabs={DIRECTORY_TABS.map((tab) => ({
                  ...tab,
                  badge:
                    tab.id === 'users'
                      ? directory.users.length
                      : tab.id === 'groups'
                        ? directory.groups.length
                        : tab.id === 'mappings'
                          ? directory.mappings.length
                          : undefined,
                }))}
                activeTab={directoryTab}
                onChange={setDirectoryTab}
                ariaLabel="Directory data sections"
              />

              {directoryTab !== 'advanced' && (
                <div className="w-full lg:max-w-sm">
                  <SearchInput
                    value={directorySearch}
                    onChange={setDirectorySearch}
                    placeholder={`Search ${directoryTab}`}
                    ariaLabel={`Search directory ${directoryTab}`}
                  />
                </div>
              )}
            </div>
          </div>

          {directoryTab === 'users' && (
            <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-6 py-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="font-black text-slate-950">Synchronized Microsoft users</h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Eligibility comes from Microsoft assignment. Effective access comes from local PII groups.
                    </p>
                  </div>
                  <Link
                    to="/access-control/groups"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700"
                  >
                    <KeyRound className="h-4 w-4" />
                    Assign local access
                  </Link>
                </div>
              </div>

              <div className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
                {filteredUsers.length ? (
                  filteredUsers.map((user) => {
                    const ready = Boolean(user.ready_for_login || user.sync_status === 'ready');
                    return (
                      <article key={user.id} className="rounded-2xl border border-slate-200 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-black text-slate-950">
                              {user.display_name || user.full_name || user.email || 'Unnamed user'}
                            </p>
                            <p className="mt-1 truncate text-xs text-slate-500">
                              {user.email || user.user_principal_name || 'No email returned'}
                            </p>
                          </div>
                          <StatusPill
                            label={user.is_stale ? 'Stale' : ready ? 'SSO ready' : 'Needs local group'}
                            tone={user.is_stale ? 'warning' : ready ? 'success' : 'neutral'}
                          />
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-500">
                          {user.department && <span className="rounded-full bg-slate-100 px-2 py-1">{user.department}</span>}
                          {user.account_enabled === false && (
                            <span className="rounded-full bg-red-50 px-2 py-1 text-red-700">Disabled in Microsoft</span>
                          )}
                          {user.local_user_id && (
                            <span className="rounded-full bg-blue-50 px-2 py-1 text-blue-700">Local identity linked</span>
                          )}
                        </div>
                      </article>
                    );
                  })
                ) : (
                  <div className="md:col-span-2 xl:col-span-3">
                    <EmptyState
                      icon={Users}
                      title="No matching directory users"
                      description="Run synchronization or adjust the search term."
                    />
                  </div>
                )}
              </div>
            </section>
          )}

          {directoryTab === 'groups' && (
            <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-6 py-5">
                <h2 className="font-black text-slate-950">Synchronized Microsoft groups</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Group assignment availability depends on the customer Microsoft Entra plan. Individual-user sync remains supported.
                </p>
              </div>
              <div className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
                {filteredGroups.length ? (
                  filteredGroups.map((group) => (
                    <article key={group.id} className="rounded-2xl border border-slate-200 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black text-slate-950">{group.display_name}</p>
                          <p className="mt-1 text-xs text-slate-500">{group.member_count || 0} direct members</p>
                        </div>
                        <StatusPill label={group.is_stale ? 'Stale' : 'Current'} tone={group.is_stale ? 'warning' : 'success'} />
                      </div>
                      {group.description && <p className="mt-3 line-clamp-2 text-xs leading-5 text-slate-500">{group.description}</p>}
                    </article>
                  ))
                ) : (
                  <div className="md:col-span-2 xl:col-span-3">
                    <EmptyState
                      icon={Cloud}
                      title="No matching Microsoft groups"
                      description="Your Entra plan may support individual assignments only, which does not prevent user synchronization."
                    />
                  </div>
                )}
              </div>
            </section>
          )}

          {directoryTab === 'mappings' && (
            <div className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
              <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-black text-slate-950">Create group mapping</h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  New mappings add local access during successful syncs. Deleting a mapping does not revoke existing memberships.
                </p>

                <div className="mt-5 space-y-4">
                  <label className="block text-sm font-bold text-slate-700">
                    Microsoft group
                    <select
                      value={mappingForm.directory_group}
                      onChange={(event) => setMappingForm((previous) => ({ ...previous, directory_group: event.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    >
                      <option value="">Select current Microsoft group</option>
                      {directory.groups.filter((group) => !group.is_stale).map((group) => (
                        <option key={group.id} value={group.id}>{group.display_name}</option>
                      ))}
                    </select>
                  </label>

                  <label className="block text-sm font-bold text-slate-700">
                    PII Platform group
                    <select
                      value={mappingForm.pii_group}
                      onChange={(event) => setMappingForm((previous) => ({ ...previous, pii_group: event.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    >
                      <option value="">Select local authorization group</option>
                      {directory.pii_groups.map((group) => (
                        <option key={group.id} value={group.id}>{group.name}</option>
                      ))}
                    </select>
                  </label>

                  <button
                    type="button"
                    onClick={createMapping}
                    disabled={actionLoading === 'create-mapping'}
                    className="w-full rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700 disabled:opacity-60"
                  >
                    {actionLoading === 'create-mapping' ? 'Creating mapping…' : 'Create mapping'}
                  </button>
                </div>
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-black text-slate-950">Current mappings</h2>
                <p className="mt-1 text-sm text-slate-500">Review the authorization bridge between Microsoft and local RBAC.</p>

                <div className="mt-5 space-y-3">
                  {directory.mappings.length ? (
                    directory.mappings.map((mapping) => (
                      <div key={mapping.id} className="flex flex-col gap-3 rounded-2xl border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black text-slate-950">
                            {mapping.directory_group_name || mapping.microsoft_group_name || 'Microsoft group'}
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            → {mapping.pii_group_name || mapping.local_group_name || 'PII Platform group'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteMapping(mapping.id)}
                          disabled={actionLoading === `delete-mapping:${mapping.id}`}
                          className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-100 disabled:opacity-60"
                        >
                          Delete mapping
                        </button>
                      </div>
                    ))
                  ) : (
                    <EmptyState
                      icon={KeyRound}
                      title="No Microsoft group mappings"
                      description="This is expected when your Entra plan supports individual application assignments only. Assign synchronized users directly from Groups & Permissions."
                      action={(
                        <Link to="/access-control/groups" className="inline-flex rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700">
                          Open Groups & Permissions
                        </Link>
                      )}
                    />
                  )}
                </div>
              </section>
            </div>
          )}

          {directoryTab === 'advanced' && (
            <div className="grid gap-6 xl:grid-cols-2">
              <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-black text-slate-950">Synchronization mode</h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Assigned Enterprise Application users is the preferred mode for your current individual-assignment flow.
                </p>

                <div className="mt-5 space-y-3">
                  {(syncSetup.available_modes || [
                    { value: 'assigned_enterprise_app', label: 'Assigned Enterprise Application users and groups' },
                    { value: 'selected_groups', label: 'Selected Microsoft groups' },
                  ]).map((mode) => {
                    const selected = (syncSetup.mode || sync.mode) === mode.value;
                    return (
                      <button
                        key={mode.value}
                        type="button"
                        onClick={() => handleSyncMode(mode.value)}
                        disabled={actionLoading === 'sync-mode'}
                        className={cx(
                          'flex w-full items-start gap-3 rounded-2xl border p-4 text-left',
                          selected ? 'border-orange-200 bg-orange-50' : 'border-slate-200 hover:bg-slate-50',
                        )}
                      >
                        <span className={cx('mt-0.5 h-4 w-4 rounded-full border-4', selected ? 'border-orange-600 bg-white' : 'border-slate-300 bg-white')} />
                        <span>
                          <span className="block text-sm font-black text-slate-950">{mode.label}</span>
                          <span className="mt-1 block text-xs leading-5 text-slate-500">
                            {mode.value === 'assigned_enterprise_app'
                              ? 'Imports users and available groups assigned to the Enterprise Application.'
                              : 'Imports direct members from an explicit Microsoft group object ID list.'}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h2 className="text-lg font-black text-slate-950">Manual user import</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Use for recovery or validation. Enter Microsoft object IDs or UPNs separated by commas or new lines.
                  </p>
                  <textarea
                    value={manualUsersText}
                    onChange={(event) => setManualUsersText(event.target.value)}
                    rows={4}
                    placeholder="finance.user@example.com\n11111111-1111-1111-1111-111111111111"
                    className="mt-4 w-full resize-none rounded-xl border border-slate-300 px-3 py-2.5 font-mono text-xs outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                  <button
                    type="button"
                    onClick={handleManualUserImport}
                    disabled={actionLoading === 'manual-users'}
                    className="mt-3 w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  >
                    {actionLoading === 'manual-users' ? 'Queuing…' : 'Queue manual user sync'}
                  </button>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h2 className="text-lg font-black text-slate-950">Selected-group import</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Direct members only. Nested Microsoft groups are not silently expanded.
                  </p>
                  <textarea
                    value={groupIdsText}
                    onChange={(event) => setGroupIdsText(event.target.value)}
                    rows={4}
                    placeholder="Microsoft group object ID"
                    className="mt-4 w-full resize-none rounded-xl border border-slate-300 px-3 py-2.5 font-mono text-xs outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                  <button
                    type="button"
                    onClick={handleSelectedGroupImport}
                    disabled={actionLoading === 'manual-groups'}
                    className="mt-3 w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  >
                    {actionLoading === 'manual-groups' ? 'Queuing…' : 'Queue selected-group sync'}
                  </button>
                </div>
              </section>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
